import React, { useEffect, useMemo, useState } from 'react'

import { ArrowRepeat, BuildingAdd, Database, Eyeglasses, Recycle, List, Trash, Tag, Display } from 'react-bootstrap-icons'
import { SendResultsProps, Action } from './SendResults.types'

import { checkPollinationPanel, getHost, useSendMessage } from '../hooks'

import { ResultsAction } from '../hooks/types'
import { BaseDropdown, Option } from '../atoms/BaseDropdown/BaseDropdown'
import { Button } from '../atoms'

import { useDebounce } from '../hooks/useDebounce'

const getAction = (key: string): ResultsAction => {
  switch (key) {
    case 'add':
      return 'AddResults'
    case 'delete':
      return 'DeleteResults'
    case 'preview':
      return 'PreviewResults'
    case 'clear':
      return 'ClearResults'
    case 'subscribe-preview':
      return 'SubscribePreviewResults'
  }
}

interface DataSetSelection {
  identifier: string
  activeData: number
  labels: string[]
  options: Option[]
}

const overlayOptions: Option[] = [
  {
    type: 'label',
    id: Action.preview,
    label: 'Preview',
    icon: <Eyeglasses size={16} />,
  },
  {
    type: 'label',
    id: Action.add,
    label: 'Add',
    icon: <BuildingAdd size={16} />,
  },
  {
    type: 'label',
    id: Action.delete,
    label: 'Delete',
    icon: <Trash size={16} />,
  },
  {
    type: 'label',
    id: Action.clear,
    label: 'Clear',
    icon: <Recycle size={16} />,
  },
  {
    type: 'label',
    id:  Action.subscribePreview,
    label: 'Subscribe Preview',
    icon: <ArrowRepeat size={16} />,
  },
]

export const SendResults: React.FC<SendResultsProps> = ({
  results,
  defaultAction,
  defaultKey,
  optionsConfig,
  geometryOptions,
  onOverlayHeightChange = (height: number) => undefined,
  delay = 500
}) => {
  const [key, setKey] = useState(defaultKey ?? performance.now().toString())
  const [currOption, setCurrOption] = useState<Option>()
  const [previewResults, setPreviewResults] = useState<any>()

  const debounce = useDebounce(delay)

  useEffect(() => {
    // if (!results || Object.keys(results).length === 0) return

    setPreviewResults(results) // restart preview and index
  }, [results, currOption])

  const onSelect = (o: any) => setCurrOption(o)

  const host = getHost()
  const { sendMessage } = useSendMessage()

  const disabled = useMemo(() => {
    return !checkPollinationPanel()    
  }, [])

  const values = useMemo(() => {
    if (!optionsConfig) return overlayOptions

    return overlayOptions.filter(v => optionsConfig[v.id] === true || optionsConfig[v.id] === undefined)
  }, [optionsConfig])

  useEffect(() => {
    const defaultVal = overlayOptions.filter(v => v.id === defaultAction)
    if (!defaultVal) return

    setCurrOption(defaultVal[0])
  }, [defaultAction])

  useEffect(() => {
    setKey(defaultKey)
  }, [defaultKey])

  debounce(() => {
    if (disabled || currOption?.id !== Action.subscribePreview) return
    sendMessage(
      'SubscribePreviewResults',
      {
        data: previewResults,
        uniqueId: key,
        options: geometryOptions,
      },
    )

    return () => {
      sendMessage(
        'ClearResults',
        {
          data: previewResults,
          uniqueId: key,
          options: geometryOptions,
        },
      )
    }
  }, [disabled, geometryOptions, key, previewResults, currOption, sendMessage])

  const dataSets = useMemo(() => {
    if (!previewResults || Object.keys(previewResults).length === 0) return []

    if (currOption?.id !== Action.preview && currOption?.id !== Action.subscribePreview) {
      sendMessage(
        'ClearResults',
        {
          data: previewResults,
          uniqueId: key,
          options: geometryOptions,
        },
      )
    }

    const res = JSON.parse(JSON.stringify(previewResults)) as any
    const analysisGeometry: any[] = res?.geometry?.filter(g => g.type === 'AnalysisGeometry')
    if (!analysisGeometry || analysisGeometry?.length === 0) return []
    
    return analysisGeometry.map((a: any) => {
      const labels = a?.data_sets?.map((d, index) => d?.data_type?.name ?? `Data-${index}`) ?? []
      
      return {
        identifier: a?.identifier,
        activeData: a?.active_data,
        labels: labels,
        options: labels?.map((l, index) => { 
          return { 
            type: 'label', 
            id: `${index}`, 
            label: l,
            category: a?.identifier,
        } as Option })
      } as DataSetSelection
    })
  }, [previewResults])

  const initialIds = useMemo(() => {
    if (dataSets.length === 0) return

    return dataSets.map(dt => dt.activeData)
  }, [dataSets])

  const onSelectDataSet = (o: any) => {
    const clone = JSON.parse(JSON.stringify(previewResults)) as any

    clone.geometry.forEach(geo => {
      if (geo?.identifier === o.category) {
        geo['active_data'] = Number(o.id)
      }
    })
    triggerAction(clone)
    setPreviewResults(clone)
  }

  const triggerAction = (data: any) => {
    if (currOption.id !== Action.preview) return
    sendMessage(
      getAction(Action.clear),
      {
        data: data,
        uniqueId: key,
        options: geometryOptions,
      },
    )
    sendMessage(
      getAction(Action.preview),
      {
        data: data,
        uniqueId: key,
        options: geometryOptions,
      },
    )
  }
  

  const helpTextButton = {
    add: `Add results to the ${host} viewport`,
    preview: `Preview results in the ${host} viewport`,
    delete: `Delete results from the ${host} viewport`,
    clear: `Clear results from the ${host} viewport`,
    'subscribe-preview': `Render results in the ${host} viewport`,
  }

  return <BaseDropdown 
    onOverlayHeightChange={onOverlayHeightChange}
    childOverlayStyle = {{ 
      width: 'auto', 
      backgroundColor: 'white',
      color: '#393e46',
      fontSize: '12px',
      padding: '10px',
      cursor: 'pointer'
    }}
    triggerStyle={{
      border: 'solid 1px #fff',
      flexShrink: '0',
    }}
    placement={'bottomRight'}
    activeId={defaultAction}
    action={'click'}
    disabled={disabled}
    onSelect={onSelect}
    options={values}>
    <Button
      title={(
        (currOption?.id === Action.preview 
          || currOption?.id === Action.subscribePreview)
          && (dataSets.length >= 1)
          ? dataSets?.map((d, index) => `${d.labels[initialIds[index]]}`).join(' & ') 
          : helpTextButton[currOption?.id])}
      disabled={disabled}
      style={{
        width: '100%',
        fontSize: '14px',
        border: 'solid 1px #fff',
      }}
      onClick={(e: any) => {
        e.stopPropagation()
        if (disabled) return

        const usePreviewData = (currOption?.id === Action.preview 
          || currOption?.id === Action.subscribePreview)
        
        sendMessage(
          getAction(currOption?.id),
          {
            data: usePreviewData ? previewResults : results,
            uniqueId: key,
            options: geometryOptions,
          },
        )
      }}
    >
      {!disabled && <span
        style={{
          flexShrink: 0,
          display: 'flex'
        }}>{currOption?.icon}</span>}
      {!disabled && 
        (currOption?.id !== Action.preview && currOption?.id !== Action.subscribePreview)
        && (currOption?.label ?? 'Pollination')}
      {!disabled && 
        (currOption?.id === Action.preview || currOption?.id === Action.subscribePreview)
        && <span
          style={{
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
          }}>{ (dataSets.length >= 1 
            ? dataSets?.map((d, index) => `${d.labels[initialIds[index]]}`).join(' & ') 
            : (currOption?.label ?? 'Pollination')) }
          </span>}
      {disabled && <span
        style={{ color: 'red' }}
      >Enabled in Pollination CAD plugins</span>}
    </Button>
    {currOption?.id 
        && (currOption?.id === Action.preview || currOption?.id === Action.subscribePreview)
        && initialIds
        && dataSets.length >= 1 && dataSets?.map((d, index) => <BaseDropdown 
        onOverlayHeightChange={onOverlayHeightChange}
        childOverlayStyle = {{ 
          width: 'auto', 
          backgroundColor: 'white',
          color: '#393e46',
          fontSize: '12px',
          padding: '10px',
          cursor: 'pointer'
        }}
        icon={<List size={10} color='#fff'/>}
        triggerStyle={{
          border: 'solid 1px #fff',
          flexShrink: '0',
          height: '42px'
        }}
        placement={'bottomRight'}
        activeId={initialIds && initialIds[index]?.toString()}
        action={'click'}
        disabled={disabled}
        onSelect={onSelectDataSet}
        options={d.options} />)}
  </BaseDropdown>
}

export default SendResults